/**
 * Report macros — typing triggers and voice aliases that expand into
 * pre-written text. Stored under `user_settings.reportMacros` as a JSON array,
 * so clinic-wide vs personal-override scope works the same as other settings.
 */
import { loadSettingsFull, saveUserSettings, SettingScope } from './userSettings';

export interface Macro {
  /** Stable local id (timestamp string) — not needed on the server */
  id: string;
  /** Typed trigger WITHOUT the leading backslash. e.g. "lungnormal" */
  trigger: string;
  /** Voice aliases — spoken phrases that trigger the expansion. Case-insensitive. */
  voiceAliases: string[];
  /** Text to insert. Newlines preserved. */
  expansion: string;
  /** Optional modality filter (e.g. "CT"). Blank = available for any modality. */
  modality?: string;
  /** Optional description for the macro picker list */
  description?: string;
}

export function newMacroId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export async function loadMacros(): Promise<{ macros: Macro[]; scope?: SettingScope }> {
  const s = await loadSettingsFull();
  const raw = s.values.reportMacros;
  if (!raw) return { macros: [], scope: undefined };
  try {
    const list = JSON.parse(raw);
    return {
      macros: Array.isArray(list) ? list : [],
      scope: s.scope.reportMacros,
    };
  } catch {
    return { macros: [], scope: s.scope.reportMacros };
  }
}

export async function saveMacros(macros: Macro[], scope: SettingScope): Promise<boolean> {
  return saveUserSettings({ reportMacros: JSON.stringify(macros) }, scope);
}

/** Find a macro whose typed trigger matches (case-insensitive, without leading backslash). */
export function findMacroByTrigger(macros: Macro[], token: string): Macro | undefined {
  if (!token) return undefined;
  const needle = token.replace(/^\\/, '').toLowerCase();
  return macros.find((m) => m.trigger.toLowerCase() === needle);
}

/** Find a macro whose voice alias matches a spoken phrase (case-insensitive exact). */
export function findMacroByVoice(macros: Macro[], phrase: string): Macro | undefined {
  if (!phrase) return undefined;
  const needle = phrase.trim().toLowerCase().replace(/[.,!?]+$/, '');
  return macros.find((m) => m.voiceAliases.some((a) => a.trim().toLowerCase() === needle));
}
